/**
 * Cupid Tank — Mark-then-punish close-range predator.
 * Shot 1: Cupid's Arrow — fast homing arrow that marks nearest enemy on hit.
 * Shot 2: Heartbeat — two-pulse heart-shaped emanation from Cupid's body, bonus damage to marked targets.
 */

import { registerWeapon } from '../weapons';
import { registerTank } from './registry';
import { DEFAULT_CRATER } from '../terrain/crater-shapes';
import { KNOCKBACK } from '../constants';
import { mirrorDIFreeze } from '../physics/transitions';
import { cancelGrapple } from '../game/grapple';
import { recordTrickHit } from '../game/trick-score';

// ── Heart parametric curve for particle placement ──
// Standard heart: x = 16sin³(t), y = -(13cos(t) - 5cos(2t) - 2cos(3t) - cos(4t))
// Returns [x, y] normalized to unit scale (~16px max), caller multiplies by desired radius
function heartPoint(t: number): [number, number] {
  const sinT = Math.sin(t);
  const x = 16 * sinT * sinT * sinT;
  const y = -(13 * Math.cos(t) - 5 * Math.cos(2 * t) - 2 * Math.cos(3 * t) - Math.cos(4 * t));
  return [x / 16, y / 16]; // normalize so max extent ~ 1
}

// ── Heartbeat phase IDs ──
const PHASE_BEAT1 = 0;
const PHASE_PAUSE = 1;
const PHASE_BEAT2 = 2;
const PHASE_CLEANUP = 3;

// ── Shot 1: Cupid's Arrow — fast arc projectile with gentle homing + mark on impact ──
const CUPID_SHOT1 = registerWeapon({
  id: 'cupid_shot1',
  name: 'Cupid\'s Arrow',
  description: 'Fast homing arrow that marks the nearest enemy on impact.',
  tier: 'CHEAP',
  rarity: 'common',
  projectileRadius: 7,
  projectileSpeed: 1.15,
  color: 0xff5588,
  gravity: 0.08,
  projectileShape: 'arrow',
  bounces: 0,
  bounceEnergyRetention: 0,
  damage: 18,
  blastRadius: 90,
  knockbackMult: 0.07,
  knockbackRadius: 1.2,
  shakeIntensity: 2.0,
  particleScale: 1.2,
  craterShape: DEFAULT_CRATER,
  edgeguard: { chargeFrames: 30, threshold: 45 },
  // Gentle homing: steer velocity toward nearest enemy each frame + pink trail
  onTick(proj, state, fx) {
    const bs = proj.behaviorState;
    bs.t = (bs.t || 0) + 1;

    // Pink trail particles every 2 frames
    if (bs.t % 2 === 0) {
      fx.push({ type: 'particles', method: 'spawnParticle', args: [
        proj.x, proj.y, 0xff88aa,
        0.15, 0.5, 0.06, 0.12, 0.15, 0.35, 0, 0.93, true, false, 0.4,
      ] });
    }

    // Find nearest alive enemy
    let bestDist = Infinity;
    let bestIdx = -1;
    for (let i = 0; i < state.players.length; i++) {
      if (i === proj.owner) continue;
      const p = state.players[i];
      if (!p.alive) continue;
      const dx = p.x - proj.x;
      const dy = p.y - proj.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist < bestDist) {
        bestDist = dist;
        bestIdx = i;
      }
    }

    // Steer velocity toward target (~3° max correction per frame)
    if (bestIdx >= 0) {
      const target = state.players[bestIdx];
      const currentAngle = Math.atan2(proj.vy, proj.vx);
      const desiredAngle = Math.atan2(target.y - proj.y, target.x - proj.x);
      let diff = desiredAngle - currentAngle;
      while (diff > Math.PI) diff -= Math.PI * 2;
      while (diff < -Math.PI) diff += Math.PI * 2;
      const maxCorrection = 3 * (Math.PI / 180);
      if (diff > maxCorrection) diff = maxCorrection;
      if (diff < -maxCorrection) diff = -maxCorrection;

      const speed = Math.sqrt(proj.vx * proj.vx + proj.vy * proj.vy);
      const newAngle = currentAngle + diff;
      proj.vx = Math.cos(newAngle) * speed;
      proj.vy = Math.sin(newAngle) * speed;
    }

    return null;
  },
  // Mark nearest enemy within 150px of impact
  onImpact(proj, x, y, _hitType, _hitIndex, state, fx) {
    let bestDist = 150;
    let bestIdx = -1;
    for (let i = 0; i < state.players.length; i++) {
      if (i === proj.owner) continue;
      const p = state.players[i];
      if (!p.alive) continue;
      const dx = p.x - x;
      const dy = p.y - y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist < bestDist) {
        bestDist = dist;
        bestIdx = i;
      }
    }

    if (bestIdx >= 0) {
      const target = state.players[bestIdx];
      target.cupidMarkTurns = 3;
      target.cupidMarkedBy = proj.owner;

      // Mark-application VFX — pulsing ring + heart burst + sparkles
      fx.push({ type: 'particles', method: 'spawnCupidMarkApplied', args: [target.x, target.y - 10] });
      fx.push({ type: 'screen_shake', intensity: 3, decay: 0.90 });
    }

    return [];
  },
});

// ── Shot 2: Heartbeat — lifecycle-manager two-pulse radial emanation ──
const CUPID_SHOT2 = registerWeapon({
  id: 'cupid_shot2',
  name: 'Heartbeat',
  description: 'Two-pulse radial emanation. Bonus damage to marked targets.',
  tier: 'UNCOMMON',
  rarity: 'uncommon',
  projectileRadius: 3,
  projectileSpeed: 0.01,
  color: 0xff5588,
  gravity: 0,
  bounces: 0,
  bounceEnergyRetention: 0,
  damage: 12,
  blastRadius: 160,
  knockbackMult: 0.09,
  knockbackRadius: 1.2,
  shakeIntensity: 2.0,
  particleScale: 1.0,
  noCrater: true,
  craterShape: DEFAULT_CRATER,
  tracerMode: 'radial',
  edgeguard: { chargeFrames: 30, threshold: 45 },
  onTick(proj, state, fx) {
    proj.ignoreTerrain = true;
    proj.ignoreTanks = true;
    proj.ignoreWind = true;

    const owner = state.players[proj.owner];
    if (!owner) return 'consume';

    const bs = proj.behaviorState;

    // ── First frame: initialize ──
    if (bs.initialized === undefined) {
      bs.initialized = 1;
      bs.phase = PHASE_BEAT1;
      bs.frame = 0;
      bs.beat1Hit = 0; // bitmask of players hit by beat 1
    }

    // Anchor projectile to Cupid's position
    proj.x = owner.x;
    proj.y = owner.y;

    const phase = bs.phase;
    const frame = bs.frame;

    // ══════════════════════════════════════════════════════
    // Phase 0: FIRST BEAT (frames 0-12)
    // ══════════════════════════════════════════════════════
    if (phase === PHASE_BEAT1) {
      // Expanding heart-shaped particle pulse
      const ringT = frame / 12;
      const heartScale = ringT * 100;
      if (frame % 2 === 0) {
        const count = 16;
        for (let k = 0; k < count; k++) {
          const t = (k / count) * Math.PI * 2;
          const [hx, hy] = heartPoint(t);
          const px = owner.x + hx * heartScale;
          const py = owner.y + hy * heartScale;
          // Outward velocity along heart contour
          const ovx = hx * 0.03;
          const ovy = hy * 0.03;
          fx.push({ type: 'particles', method: 'spawnParticle', args: [
            px, py, 0xff5588,
            0.2, 0.5, 0.06, 0.12, ovx, ovy, 0, 0.94, true, false, 0.4,
          ] });
        }
      }

      // Frame 10: damage check — 100px radius
      if (frame === 10) {
        for (let i = 0; i < state.players.length; i++) {
          if (i === proj.owner) continue;
          const p = state.players[i];
          if (!p.alive) continue;
          if (p.dashImmune > 0) continue;

          const dx = p.x - owner.x;
          const dy = p.y - owner.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist > 100) continue;

          // Mark bonus: 18 damage if marked, 12 if not
          const isMarked = p.cupidMarkTurns > 0 && p.cupidMarkedBy === proj.owner;
          const damage = isMarked ? 18 : 12;

          p.damagePercent += damage;
          bs.beat1Hit |= (1 << i); // track who got hit

          // Match stats
          if (state.matchStats) {
            state.matchStats.damageDealt[proj.owner] += damage;
            state.matchStats.damageTaken[i] += damage;
            if (damage > state.matchStats.bestSingleHit[proj.owner]) {
              state.matchStats.bestSingleHit[proj.owner] = damage;
            }
            p.lastHitBy = proj.owner;
          }

          // Knockback away from Cupid
          if (state.grapple.active?.playerIndex === i) cancelGrapple(state);
          const hitDist = Math.max(dist, 1);
          const percentMult = 1 + p.damagePercent / KNOCKBACK.KBG;
          const kbForce = proj.weapon.blastRadius * proj.weapon.knockbackMult * 0.5 * percentMult;
          p.diPendingVx = (dx / hitDist) * kbForce;
          p.diPendingVy = (dy / hitDist) * kbForce;
          mirrorDIFreeze(p);
          p.settled = false;
          state.firingPhaseKnockedPlayers.push(i);

          fx.push({ type: 'tank_flash', playerIndex: i, bright: 0xff5588, dark: 0x220011, duration: 0.25 });
          // Per-player heartbeat impact burst (beat 1)
          fx.push({ type: 'particles', method: 'spawnCupidHeartbeatHit', args: [p.x, p.y, isMarked, false] });
          recordTrickHit(state, proj, i, p.x, p.y, damage, false, fx);
        }

        // Small screen shake + heart burst at damage frame
        fx.push({ type: 'screen_shake', intensity: 5, decay: 0.88 });
        for (let k = 0; k < 20; k++) {
          const t = (k / 20) * Math.PI * 2;
          const [hx, hy] = heartPoint(t);
          fx.push({ type: 'particles', method: 'spawnParticle', args: [
            owner.x + hx * 100, owner.y + hy * 100, 0xff88aa,
            0.25, 0.55, 0.07, 0.14, hx * 0.04, hy * 0.04, 0, 0.93, true, false, 0.5,
          ] });
        }
      }

      bs.frame++;
      if (bs.frame > 12) {
        bs.phase = PHASE_PAUSE;
        bs.frame = 0;
      }
      return null;
    }

    // ══════════════════════════════════════════════════════
    // Phase 1: PAUSE (frames 13-25)
    // ══════════════════════════════════════════════════════
    if (phase === PHASE_PAUSE) {
      // Lingering heart particles
      if (frame % 4 === 0) {
        const angle = Math.random() * Math.PI * 2;
        const dist = 20 + Math.random() * 40;
        fx.push({ type: 'particles', method: 'spawnParticle', args: [
          owner.x + Math.cos(angle) * dist,
          owner.y + Math.sin(angle) * dist,
          0xff88aa,
          0.2, 0.4, 0.05, 0.1, 0, -0.02, -0.005, 0.96, true, false, 0.4,
        ] });
      }

      bs.frame++;
      if (bs.frame > 12) {
        bs.phase = PHASE_BEAT2;
        bs.frame = 0;
      }
      return null;
    }

    // ══════════════════════════════════════════════════════
    // Phase 2: SECOND BEAT (frames 26-38)
    // ══════════════════════════════════════════════════════
    if (phase === PHASE_BEAT2) {
      // Bigger expanding heart-shaped particle pulse (denser)
      const ringT = frame / 12;
      const heartScale = ringT * 160;
      if (frame % 2 === 0) {
        const count = 24; // denser than beat 1
        for (let k = 0; k < count; k++) {
          const t = (k / count) * Math.PI * 2;
          const [hx, hy] = heartPoint(t);
          const px = owner.x + hx * heartScale;
          const py = owner.y + hy * heartScale;
          const ovx = hx * 0.035;
          const ovy = hy * 0.035;
          fx.push({ type: 'particles', method: 'spawnParticle', args: [
            px, py, 0xff3366,
            0.25, 0.55, 0.07, 0.14, ovx, ovy, 0, 0.93, true, false, 0.45,
          ] });
        }
      }

      // Frame 10 of this phase (= frame 36 overall): damage check — 160px radius
      if (frame === 10) {
        let markedHitByBoth = false;

        for (let i = 0; i < state.players.length; i++) {
          if (i === proj.owner) continue;
          const p = state.players[i];
          if (!p.alive) continue;
          if (p.dashImmune > 0) continue;

          const dx = p.x - owner.x;
          const dy = p.y - owner.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist > 160) continue;

          // Mark bonus: 33 damage if marked, 22 if not
          const isMarked = p.cupidMarkTurns > 0 && p.cupidMarkedBy === proj.owner;
          const damage = isMarked ? 33 : 22;

          p.damagePercent += damage;

          // Consume mark after Beat 2 damage (both beats get the bonus, then it's spent)
          if (isMarked) {
            p.cupidMarkTurns = 0;
            p.cupidMarkedBy = -1;
          }

          // Match stats
          if (state.matchStats) {
            state.matchStats.damageDealt[proj.owner] += damage;
            state.matchStats.damageTaken[i] += damage;
            if (damage > state.matchStats.bestSingleHit[proj.owner]) {
              state.matchStats.bestSingleHit[proj.owner] = damage;
            }
            p.lastHitBy = proj.owner;
          }

          // Knockback away from Cupid
          if (state.grapple.active?.playerIndex === i) cancelGrapple(state);
          const hitDist = Math.max(dist, 1);
          const percentMult = 1 + p.damagePercent / KNOCKBACK.KBG;
          const kbForce = proj.weapon.blastRadius * proj.weapon.knockbackMult * percentMult;
          p.diPendingVx = (dx / hitDist) * kbForce;
          p.diPendingVy = (dy / hitDist) * kbForce;
          mirrorDIFreeze(p);
          p.settled = false;
          state.firingPhaseKnockedPlayers.push(i);

          fx.push({ type: 'tank_flash', playerIndex: i, bright: 0xff3366, dark: 0x220011, duration: 0.4 });
          // Per-player heartbeat impact burst (beat 2)
          fx.push({ type: 'particles', method: 'spawnCupidHeartbeatHit', args: [p.x, p.y, isMarked, true] });
          recordTrickHit(state, proj, i, p.x, p.y, damage, false, fx);

          // Check HEARTBREAK: marked target hit by both beats
          if (isMarked && (bs.beat1Hit & (1 << i))) {
            markedHitByBoth = true;
            fx.push({ type: 'announce_world', text: 'HEARTBREAK!', x: p.x, y: p.y - 60, color: 0xff2266, fontSize: 40, hold: 1.0 });
            // HEARTBREAK detonation — combo finisher VFX (tipper-level impact)
            fx.push({ type: 'particles', method: 'spawnCupidHeartbreak', args: [p.x, p.y] });
          }
        }

        // Bigger screen shake + large heart burst at damage frame
        const shakeIntensity = markedHitByBoth ? 14 : 8;
        fx.push({ type: 'screen_shake', intensity: shakeIntensity, decay: 0.82 });
        for (let k = 0; k < 28; k++) {
          const t = (k / 28) * Math.PI * 2;
          const [hx, hy] = heartPoint(t);
          fx.push({ type: 'particles', method: 'spawnParticle', args: [
            owner.x + hx * 160, owner.y + hy * 160, 0xff3366,
            0.3, 0.6, 0.08, 0.16, hx * 0.05, hy * 0.05, 0, 0.92, true, false, 0.55,
          ] });
        }
        if (markedHitByBoth) {
          fx.push({ type: 'screen_multi_flash', color: 0xff5588, alpha: 0.2 });
        }
      }

      bs.frame++;
      if (bs.frame > 12) {
        bs.phase = PHASE_CLEANUP;
        bs.frame = 0;
      }
      return null;
    }

    // ══════════════════════════════════════════════════════
    // Phase 3: CLEANUP
    // ══════════════════════════════════════════════════════
    return 'consume';
  },
});

registerTank({
  id: 'cupid',
  name: 'Cupid',
  description: 'Mark-then-punish predator. Pierce with homing arrow, close in for Heartbeat.',
  visual: { bodyColor: 0xff5588, glowColor: 0xff88aa, bodyRadius: 33 },
  ecbRadius: 26,
  bonus: true,
  shots: [
    { tier: 1, weapon: CUPID_SHOT1, momentumCost: 0, hitlagBonus: 0, label: 'Cupid\'s Arrow', tierColor: 0xff88aa },
    { tier: 2, weapon: CUPID_SHOT2, momentumCost: 0, hitlagBonus: 0, label: 'Heartbeat', tierColor: 0xff3366 },
  ],
});
